import { useMemo } from "react";
import { useQuery } from "react-query";
import { withAqiEpa } from "./airQualityDailySeries";
import {
    ACHIEVEMENTS,
    get,
    ORGANIZATIONS,
    RANKINGS,
    RANKS,
    SETTINGS,
    SHOPS,
    USERS
} from "./Client";

export function useGetPublicOrganizations() {
    const {
        status,
        data,
        error
    } = useQuery(['public', ORGANIZATIONS], () => get('public/' + ORGANIZATIONS));
    return { status, organizations: data || [], error };
}

export function useGetPublicOrganization(id, isAll) {
    const {
        status,
        data,
        error
    } = useQuery(['public', ORGANIZATIONS, { id: id }], () => get('public/' + ORGANIZATIONS, { elem: id }),
        { enabled: !isAll }
    );
    return { status, organization: data || {}, error };
}

export function useGetPublicOrganizationSettings(id, options = {}) {
    const {
        status,
        data,
        error
    } = useQuery(['public', ORGANIZATIONS, { id: id }, SETTINGS], () => get('public/' + ORGANIZATIONS, { elem: id + "/" + SETTINGS }), { enabled: !!id && id != 'all', ...options });
    return { status, settings: data || [], error };
}

export function useGetPublicOrganizationRanks(id, isAll) {
    const {
        status,
        data,
        error
    } = useQuery(['public', ORGANIZATIONS, { id: id }, RANKINGS], () => get('public/' + ORGANIZATIONS, { elem: id + "/" + RANKINGS }),
        { enabled: isAll }
    );
    return { status, ranks: data || [], error };
}

export function useGetPublicGlobalRank(isAll, refetchOptions) {
    const {
        status,
        data,
        error
    } = useQuery(['public', RANKINGS], () => get(`public/${RANKINGS}/global`),
        { enabled: isAll, ...refetchOptions}
    );
    return { status, globalRank: data || [], error };
}

export function useGetPublicRank(id, isAll) {
    const {
        status,
        data,
        error
    } = useQuery(['public', RANKS, id], () => get('public/' + RANKINGS, { elem: id }),
        {enabled: !isAll});
    return { status, rank: data || {}, error };
}

export function useGetPublicUser(id) {
    const {
        status,
        data,
        error
    } = useQuery(['public', USERS, id], () => get('public/' + USERS, { elem: id }));
    return { status, user: data || {}, error };
}

export function useGetPublicUserAchievements(id) {
    const {
        status,
        data,
        error
    } = useQuery(['public', ACHIEVEMENTS, id], () => get('public/' + USERS, { elem: id + "/" + ACHIEVEMENTS }));
    return { status, achievements: data || [], error };
}

export function useGetPublicUserRankings(id) {
    const {
        status,
        data,
        error
    } = useQuery(['public', RANKS, id], () => get('public/' + USERS, { elem: id + "/" + RANKINGS }));
    return { status, ranks: data || [], error };
}


export function useGetPublicShops(isAll, refetchOptions) {
    const {
        status,
        data,
        error
    } = useQuery(['public', SHOPS], () => get(`public/${SHOPS}`),
        { enabled: isAll, ...refetchOptions });
    return { status, shops: data || [], error };
}

export function useHasImpactFeatures(id, isAll, refetchOptions) {
    const {
        status,
        data,
        error
    } = useQuery(['public', id, isAll], () => get((`public/has-impact-features/${id}`)),
        { enabled: !isAll, ...refetchOptions });
    return { status, hasImpactFeatures: data || false, error };
}

export function useGetImpactGeoJsonMap(id, filters, isAll, refetchOptions, isLoaded) {
    const {
        status,
        data,
        error
    } = useQuery(['public',id, filters], () => get((`public/impact-data/${id}`), {params: filters}),
        { enabled: !isAll, ...refetchOptions });
    return { status, geojson: data || [], error };
}

export function useGetImpactLineCharts(id, endDate, isAll, refetchOptions) {
    const {
        status,
        data,
        error
    } = useQuery(['public',id, endDate], () => get((`public/impact-line-charts/${id}`), {params: endDate}),
        { enabled: !isAll, ...refetchOptions });
    return { status, data , error };
}

export function useGetUgoMetricsMap(id, refetchOptions) {
  const {
    status,
    data,
    error,
  } = useQuery(
    ['public', 'ugo-metrics-map', id],
    () => get(`public/ugo-metrics-map/${id}`),
    {
      enabled: id === '66',
      ...refetchOptions,
    }
  );

  let geojson = null;

  if (data) {
    if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
      geojson = data;
    } else if (Array.isArray(data) && data[0]?.type === 'FeatureCollection') {
      // caso: l’API torna [ geojson ]
      geojson = data[0];
    } else {
      console.warn('Unexpected ugo-metrics-map payload shape', data);
    }
  }

  return { status, geojson, error };
}

export function useGetUgoMetricsData(id, params, refetchOptions) {
  const {
    status,
    data,
    error,
  } = useQuery(
    ['public', 'ugo-metrics-data', id, params],
    () => get(`public/ugo-metrics-data/${id}`, { params }),
    {
      enabled: id === '66',
      ...refetchOptions,
    }
  );

  // data è una lista di oggetti tipo:
  // { day, areaName, ozone_density, aqi_epa, pm1, pm2_5, pm10, temperature }
  const rows = useMemo(() => withAqiEpa(data), [data]);
  return { status, data: rows, error };
}
// Vista pubblica del link condivisibile del pedale: si aggiorna da sola ogni 60 secondi.
export function useGetPedalShare(token) {
    const { status, data, error } = useQuery(['public', 'pedal-share', token], () => get('ugo/public/share', { elem: token }),
        { enabled: !!token, retry: false, refetchInterval: 60 * 1000 });
    // react-query v3 tiene l'ultimo dato anche quando il refetch fallisce: a link revocato o scaduto
    // la pagina non deve continuare a mostrare la posizione vecchia.
    return { status, view: status === "error" ? null : (data || null), error };
}

// Pannello allarmi del pedale visto dal link condivisibile (stesso link, stessa scadenza della vista pubblica).
export const loadSharedPedalBoard = token => () =>
    get('ugo/public/share', { elem: `${encodeURIComponent(token)}/board` });
export const loadSharedPedalAlert = token => (imei, id) =>
    get('ugo/public/share', { elem: `${encodeURIComponent(token)}/alerts/${encodeURIComponent(imei)}/${encodeURIComponent(id)}` });

// Qualita' dell'aria di gruppo nell'Open Data (dati anonimi, solo per le iniziative abilitate).
// Come in dashboard i dati restano freschi 60 secondi e i giorni sono tutti quelli con dati.
export const OPEN_DATA_GROUP_AIR_QUALITY_ORGANIZATIONS = [79];
const PUBLIC_GROUP_AIR_QUALITY = id => `public/dash/organizations/${id}/group-air-quality`;
const PUBLIC_GROUP_AIR_QUALITY_OPTIONS = { staleTime: 60 * 1000, refetchOnWindowFocus: false };
const isGroupAirQualityEnabled = id => OPEN_DATA_GROUP_AIR_QUALITY_ORGANIZATIONS.includes(Number(id));

export function useGetPublicGroupAirQualityDays(id) {
    const { status, data, error } = useQuery(['public', 'group-air-quality', id, 'days'],
        () => get(PUBLIC_GROUP_AIR_QUALITY(id) + '/days'),
        { ...PUBLIC_GROUP_AIR_QUALITY_OPTIONS, enabled: isGroupAirQualityEnabled(id) });
    return { status, days: data || [], error };
}

// 429 oltre 60 richieste al minuto dallo stesso IP: non si riprova da soli.
export function useGetPublicGroupAirQualitySession(id, day, from, to) {
    const { status, data, error } = useQuery(['public', 'group-air-quality', id, 'session', { day, from, to }],
        () => get(PUBLIC_GROUP_AIR_QUALITY(id) + '/session', { params: { day, from, to } }),
        { ...PUBLIC_GROUP_AIR_QUALITY_OPTIONS, retry: false, enabled: isGroupAirQualityEnabled(id) && !!day });
    return { status, data, error };
}
